import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';
import agent from '../../agent';
import { sanitizeHtml } from '../../utils/sanitize';
import { getGoogleMapsLink } from '../../utils/freakUtils';

// Above the fixed mobile bottom navigation (zIndex 9999), which would otherwise
// cover the Approve / Reject buttons of a full-screen dialog.
export const MODERATION_DIALOG_Z_INDEX = 10000;

// Full content of a pending item comes from the regular detail endpoints (admins can read pending items)
const loadDetails = async (kind, slug) => {
  if (kind === 'freak') return (await agent.Freaks.get(slug))?.freak;
  if (kind === 'freakHub') return (await agent.FreakHubs.get(slug))?.freakHub;
  return null;
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    : '';

const Section = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.6 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const ImageStrip = ({ urls }) => (
  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}>
    {urls.map(url => (
      <Box
        key={url}
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ flex: '0 0 auto' }}
      >
        <Box
          component="img"
          src={url}
          alt=""
          loading="lazy"
          sx={{ height: { xs: 140, sm: 180 }, maxWidth: '80vw', objectFit: 'cover', borderRadius: 2, display: 'block' }}
        />
      </Box>
    ))}
  </Box>
);

const VideoPlayer = ({ src, poster }) => (
  <Box
    component="video"
    src={src}
    poster={poster || undefined}
    controls
    playsInline
    preload="metadata"
    sx={{ width: '100%', maxWidth: 360, maxHeight: '60vh', borderRadius: 2, display: 'block', mb: 1, mx: { xs: 'auto', sm: 0 }, background: '#000' }}
  />
);

const ModerationPreviewDialog = ({ preview, title, busy, error, onClose, onApprove, onReject }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const kind = preview?.kind;
  const item = preview?.item;

  useEffect(() => {
    setDetails(null);
    setLoadError(false);
    if (!item || kind === 'reel') return;

    let cancelled = false;
    setLoading(true);
    loadDetails(kind, item.slug)
      .then(res => !cancelled && setDetails(res))
      .catch(() => !cancelled && setLoadError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [kind, item?.slug]);

  if (!item) return null;

  // the list item is the fallback when the full details could not be loaded
  const data = { ...item, ...(details || {}) };
  const images = (data.images || []).map(img => img?.imageUrl).filter(Boolean);
  const hubImages = kind === 'freakHub' ? [data.backgroundImageUrl, data.imageUrl].filter(Boolean) : [];
  const allImages = [...hubImages, ...images];
  const videos = (data.videos || []).filter(v => v?.videoUrl);
  const coordinates = data.map?.coordinates;

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      scroll="paper"
      sx={{ zIndex: MODERATION_DIALOG_Z_INDEX }}
    >
      <DialogTitle sx={{ pr: fullScreen ? 7 : 2, pt: fullScreen ? 'calc(16px + env(safe-area-inset-top, 0px))' : 2 }}>
        {fullScreen && (
          <IconButton
            aria-label="Close preview"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 'calc(8px + env(safe-area-inset-top, 0px))', width: 44, height: 44 }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography
            component="span"
            variant="h6"
            sx={{ fontWeight: 700, overflowWrap: 'anywhere', flex: '1 1 200px', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            {title}
          </Typography>
          {data.context && <Chip size="small" label={data.context} />}
          {data.type && <Chip size="small" variant="outlined" label={data.type} />}
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          @{data.author?.username} · submitted {formatDate(data.createdAt)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, overflowX: 'hidden' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {loadError && (
          <Typography color="warning.main" variant="body2" sx={{ mb: 2 }}>
            Couldn&apos;t load the full details. Showing the summary only.
          </Typography>
        )}

        {!loading && (
          <>
            {kind === 'reel' && data.videoUrl && (
              <Section label="Video">
                <VideoPlayer src={data.videoUrl} poster={data.thumbnailUrl} />
              </Section>
            )}
            {kind === 'reel' && data.freak?.title && (
              <Section label="Attached to Freak">
                <Typography variant="body2">{data.freak.title}</Typography>
              </Section>
            )}

            {allImages.length > 0 && (
              <Section label={`Images (${allImages.length})`}>
                <ImageStrip urls={allImages} />
              </Section>
            )}

            {videos.length > 0 && (
              <Section label={`Videos (${videos.length})`}>
                {videos.map(video => <VideoPlayer key={video.videoUrl} src={video.videoUrl} />)}
              </Section>
            )}

            {(data.startDate || data.endDate) && (
              <Section label="When">
                <Typography variant="body2">
                  {formatDate(data.startDate)}
                  {data.endDate ? ` – ${formatDate(data.endDate)}` : ''}
                </Typography>
              </Section>
            )}

            {data.description && (
              <Section label="Description">
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>
                  {data.description}
                </Typography>
              </Section>
            )}

            {data.body && (
              <Section label="Content">
                <Box
                  className="freak-body"
                  sx={{
                    overflowWrap: 'anywhere',
                    // user HTML must never widen the dialog on a phone
                    '& img, & video, & iframe, & embed, & object': { maxWidth: '100%', height: 'auto' },
                    '& iframe': { aspectRatio: '16 / 9', width: '100%' },
                    '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto' },
                    '& pre': { maxWidth: '100%', overflowX: 'auto', whiteSpace: 'pre-wrap' },
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.body) }}
                />
              </Section>
            )}

            {coordinates?.latitude != null && coordinates?.longitude != null && (
              <Section label="Location">
                <Button
                  size="small"
                  startIcon={<PlaceIcon />}
                  href={getGoogleMapsLink(coordinates.latitude, coordinates.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ minHeight: 44, textTransform: 'none' }}
                >
                  {coordinates.latitude.toFixed(5)}, {coordinates.longitude.toFixed(5)}
                </Button>
              </Section>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: 'wrap',
          gap: 1,
          p: 2,
          pb: fullScreen ? 'calc(16px + env(safe-area-inset-bottom, 0px))' : 2,
          '& > :not(style) ~ :not(style)': { ml: 0 },
        }}
      >
        {error && (
          <Typography color="error" variant="body2" sx={{ flex: '1 1 100%' }}>
            {error}
          </Typography>
        )}
        {busy && <CircularProgress size={24} />}
        {!fullScreen && (
          <Button onClick={onClose} sx={{ minHeight: 44, textTransform: 'none' }}>
            Close
          </Button>
        )}
        <Button
          variant="outlined"
          color="error"
          startIcon={<CloseIcon />}
          disabled={busy}
          onClick={onReject}
          sx={{ minHeight: 44, textTransform: 'none', flex: fullScreen ? 1 : 'none' }}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
          disabled={busy || loading}
          onClick={onApprove}
          sx={{ minHeight: 44, textTransform: 'none', flex: fullScreen ? 1 : 'none' }}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModerationPreviewDialog;
